import React, { useCallback } from "react";

import cx from "classnames";

interface TextInputProps {
  className?: string;
  label?: string;
  name?: string;
  value: string;
  description?: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  type?: string;
  placeholder?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  autoFocus?: boolean;
  prefix?: React.ReactNode;
  "data-test"?: string;
}

export const TextInput: React.FC<React.PropsWithChildren<TextInputProps>> = ({
  label,
  name,
  className,
  type = "text",
  placeholder,
  description,
  value,
  onChange,
  onBlur,
  isDisabled,
  isRequired,
  autoFocus,
  prefix,
  "data-test": testId,
}) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (!onBlur) {
        return;
      }

      onBlur(event.target.value);
    },
    [onBlur],
  );

  const handleKeyPressAvoidSubmit = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    event.key === "Enter" && event.preventDefault();
  };

  return (
    <div className={className}>
      <label htmlFor="company_website">
        <span className="block text-sm text-neutral-10">{label}</span>
      </label>
      {description && (
        <span className="block mt-1 text-xs leading-tight text-neutral-20 leading-[16px]">{description}</span>
      )}
      <div className="flex mt-1 rounded-md shadow-sm">
        {prefix && (
          <span className="inline-flex items-center px-2 text-gray-500 border border-r-0 border-gray-300 rounded-l-md bg-gray-50 sm:text-sm">
            {prefix}
          </span>
        )}
        <input
          data-test={testId}
          type={type}
          value={value}
          name={name}
          onChange={handleChange}
          onKeyPress={handleKeyPressAvoidSubmit}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={isDisabled}
          required={isRequired}
          autoFocus={autoFocus}
          className={cx(
            "flex-1 min-w-0 block w-full px-2 py-2 rounded-none rounded-r-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border-black-300 border",
            !prefix && "rounded-l-md",
          )}
        />
      </div>
    </div>
  );
};
